import { useCallback, useEffect, useMemo } from 'react';

import { useRouter } from 'expo-router';
import { useMinimalCart } from 'features/cart/hooks/use-minimal-cart';
import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import { PaymentRequestError, paymentErrorType } from 'features/payment/types';
import { useStoreContext } from 'features/store';

import { IServerOrder } from '@rbi-ctg/menu';
import { logCartPlaceOrder } from 'experiments/checkout-v2/treatment/pages/cart/payment/utils';
import { useGetOrderLazyQuery } from 'generated/graphql-gateway';
import { IUseCommitOrderParams } from 'hooks/commit-order/types';
import { useCommitOrder } from 'hooks/commit-order/use-commit-order';
import { useCart } from 'hooks/use-cart';
import useErrorModal from 'hooks/use-error-modal';
import { useIOSLiveActivities } from 'hooks/use-ios-live-activities';
import { useHandleCommitError } from 'pages/cart/hooks/use-handle-commit-error';
import { useHandlePspRefusedError } from 'pages/cart/hooks/use-handle-psp-refused-error';
import { actions, useAppDispatch } from 'state/global-state';
import { t } from 'state/intl/translations';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { OrderInjectionFlowVariations } from 'state/launchdarkly/variations';
import { OrderStatus } from 'state/order/types';
import { isAndroid } from 'utils/environment';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { useCheckoutContext } from '../use-checkout-context';
import { useCheckoutLoadingState } from '../use-checkout-loading-state';

import { useBuildCommitOrderPayload } from './use-build-commit-order-payload';
import { useRequestPaymentDetails } from './use-get-payment-details';
import { useLoyaltyDiscounts } from './use-loyalty-discounts';

const alreadyCommittedRegex = /already been committed|already committed/i;

export const useCheckoutSubmit = () => {
  const orderInjectFlow = useFlag(LaunchDarklyFlag.ORDER_INJECTION_FLOW);

  const router = useRouter();
  const dispatch = useAppDispatch();

  const { store } = useStoreContext();
  const { emptyCart } = useCart();
  const { emptyMinimalCart } = useMinimalCart();
  const enableMinimalCartUi = useMinimalCartUi();
  const { startActivity: startLiveActivity } = useIOSLiveActivities();
  const {
    totals,
    paymentValues,
    isPaymentType,
    isPaymentProcessor,
    serverOrder,
    distanceToStore,
    checkoutPaymentMethodId,
    setCheckoutState,
    wasCommitOrderCalled,
  } = useCheckoutContext();
  const { subTotalCents = 0, taxCents = 0 } = totals || {};
  const { isOrderPriceLoading } = useCheckoutLoadingState();

  const { requestPayment: requestPaymentDetails } = useRequestPaymentDetails();

  const {
    buildCommitOrderPayloadWithAccount,
    buildCommitOrderPayloadWithoutAccount,
  } = useBuildCommitOrderPayload();

  const { rewardDiscountAmount, additionalDiscountAmount } = useLoyaltyDiscounts();
  const isOneTimeCardPayment = Boolean(
    paymentValues && !paymentValues.saveCard && paymentValues.cardNumber
  );

  const commitOrderParams: IUseCommitOrderParams = useMemo(
    () => ({
      rbiOrderId: serverOrder && serverOrder.rbiOrderId,
      distanceToStore,
      checkoutData: {
        savings: additionalDiscountAmount,
        rewardsSaving: rewardDiscountAmount,
        tax: taxCents,
        total: subTotalCents + taxCents,
        subtotal: subTotalCents,
      },
    }),
    [
      serverOrder,
      distanceToStore,
      additionalDiscountAmount,
      rewardDiscountAmount,
      taxCents,
      subTotalCents,
    ]
  );

  const {
    commitOrder,
    forceCommitOrder,
    // TODO: The commit error result and the commit order failure should be unified and returned as a single result.
    // https://rbictg.atlassian.net/browse/BKPE-22766
    commitResult: { error: commitError },
    loading: isCommitOrderLoading,
    success: commitOrderSuccess,
    failure: commitOrderFailure,
  } = useCommitOrder(commitOrderParams);

  const commitOrderError = commitError || commitOrderFailure;
  const [fetchOrder] = useGetOrderLazyQuery({ fetchPolicy: 'network-only' });
  const isAlreadyCommittedCommitError = useMemo(() => {
    if (!commitError) {
      return false;
    }

    if (alreadyCommittedRegex.test(commitError.message)) {
      return true;
    }

    return commitError.graphQLErrors?.some(error => alreadyCommittedRegex.test(error.message));
  }, [commitError]);

  const shouldCommitOrder = [
    OrderInjectionFlowVariations.INJECT_NOW,
    OrderInjectionFlowVariations.INJECT_AND_HOLD,
    OrderInjectionFlowVariations.TIMED_FIRE,
  ].includes(orderInjectFlow as OrderInjectionFlowVariations);

  const logContext = useMemo(
    () => ({
      isOneTimeCardPayment,
      isPaymentType,
      ...commitOrderParams,
    }),
    [isOneTimeCardPayment, isPaymentType, commitOrderParams]
  );

  const navigateToOrderConfirmation = useCallback(
    (completedOrder: IServerOrder) => {
      const orderConfirmationUrl = `${routes.orderConfirmation}/${completedOrder.rbiOrderId}?showToast=true`;

      if (isAndroid) {
        // On Android Fabric, dismissTo+push causes concurrent fragment transactions
        // that crash with "child already has a parent" (transparentModal presentation).
        // Replace is a single atomic operation that avoids this.
        router.replace(orderConfirmationUrl);
      } else {
        router.dismissTo(routes.base);
        router.push(orderConfirmationUrl);
      }
    },
    [router]
  );

  const [ErrorDialog, openErrorDialog, dismissErrorDialog] = useErrorModal({
    modalAppearanceEventMessage: 'Error: Submit Order Error',
  });
  const { handlePspRefusedError } = useHandlePspRefusedError({
    openErrorDialog,
  });

  useHandleCommitError({
    fdAccountId: checkoutPaymentMethodId ?? '',
    openErrorDialog,
    dismissErrorDialog,
    onCoolingPeriodConfirm: forceCommitOrder,
    commitError: isAlreadyCommittedCommitError || isPaymentType.cashApp ? undefined : commitError,
  });

  useEffect(() => {
    if (!commitError || isAlreadyCommittedCommitError) {
      return;
    }

    handlePspRefusedError(commitError, isPaymentType.cashApp);
  }, [commitError, isAlreadyCommittedCommitError, handlePspRefusedError, isPaymentType.cashApp]);

  const handleAlreadyCommittedOrder = useCallback(async () => {
    const orderId = serverOrder?.rbiOrderId;
    if (!orderId) {
      logger.warn({
        isPaymentLog: true,
        message: '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Missing order id',
        commitErrorMessage: commitError?.message,
        ...logContext,
      });
      return;
    }

    try {
      const { data, error } = await fetchOrder({ variables: { id: orderId } });
      if (error) {
        throw error;
      }

      const committedOrder = data?.order ? ((data.order as unknown) as IServerOrder) : null;
      const isOrderCommitted =
        committedOrder?.status === OrderStatus.INSERT_SUCCESSFUL ||
        committedOrder?.status === OrderStatus.UPDATE_SUCCESSFUL;

      if (!committedOrder || !isOrderCommitted) {
        logger.warn({
          isPaymentLog: true,
          message:
            '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Order not committed after retry',
          orderId,
          status: committedOrder?.status ?? null,
          ...logContext,
        });
        openErrorDialog({ message: t('orderFailureMessage') });
        return;
      }

      setCheckoutState(prev => ({ ...prev, serverOrder: committedOrder }));

      if (enableMinimalCartUi) {
        emptyMinimalCart();
      } else {
        emptyCart();
      }
      startLiveActivity(committedOrder);
      navigateToOrderConfirmation(committedOrder);
    } catch (error) {
      logger.error({
        isPaymentLog: true,
        error,
        message: '[useCheckoutSubmit][handleAlreadyCommittedOrder]: Error fetching order',
        orderId,
        ...logContext,
      });
      openErrorDialog({ message: t('orderFailureMessage') });
    }
  }, [
    serverOrder?.rbiOrderId,
    commitError,
    logContext,
    fetchOrder,
    openErrorDialog,
    setCheckoutState,
    emptyCart,
    emptyMinimalCart,
    enableMinimalCartUi,
    startLiveActivity,
    navigateToOrderConfirmation,
  ]);

  useEffect(() => {
    if (!isAlreadyCommittedCommitError || commitOrderSuccess) {
      return;
    }

    handleAlreadyCommittedOrder();
  }, [isAlreadyCommittedCommitError, commitOrderSuccess, handleAlreadyCommittedOrder]);

  // Handles the loading state of the order submission.
  useEffect(() => {
    const isCurrentlySubmitting =
      isCommitOrderLoading ||
      (wasCommitOrderCalled && !commitOrderSuccess && !commitOrderError) ||
      // Keep submitting state true during success to prevent early redirects
      commitOrderSuccess;

    setCheckoutState(prev => ({
      ...prev,
      isSubmittingOrder: isCurrentlySubmitting,
    }));
  }, [
    isCommitOrderLoading,
    wasCommitOrderCalled,
    commitOrderSuccess,
    setCheckoutState,
    commitOrderError,
  ]);

  // Handles order submission
  useEffect(() => {
    if (!serverOrder || (isOrderPriceLoading && !wasCommitOrderCalled)) {
      return;
    }
    if (commitOrderSuccess) {
      if (enableMinimalCartUi) {
        emptyMinimalCart();
      } else {
        emptyCart();
      }
      // Start IOS Live Activity Widget
      startLiveActivity(serverOrder);

      navigateToOrderConfirmation(serverOrder);
    }

    if (commitOrderError && serverOrder?.orderErrors && !commitError && wasCommitOrderCalled) {
      openErrorDialog({ message: t('orderFailureMessage') });
      setCheckoutState(prev => ({
        ...prev,
        wasCommitOrderCalled: false,
      }));
    }
  }, [
    store.number,
    serverOrder,
    isOrderPriceLoading,
    commitOrderSuccess,
    commitOrderError,
    emptyCart,
    emptyMinimalCart,
    enableMinimalCartUi,
    startLiveActivity,
    navigateToOrderConfirmation,
    commitError,
    openErrorDialog,
    wasCommitOrderCalled,
    setCheckoutState,
  ]);

  const submitOrder = async () => {
    if (!shouldCommitOrder) {
      logger.warn({
        isPaymentLog: true,
        message: '[useCheckoutSubmit][submitOrder]: Skipping order commit',
        shouldCommitOrder,
        ...logContext,
      });
      return;
    }

    try {
      logCartPlaceOrder();
      dispatch(actions.loyalty.setShouldRefetchOffers(true));

      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: true,
      }));

      const isOrbitalOneTimeCardPayment = isOneTimeCardPayment && isPaymentProcessor.orbital;
      // When a CashApp cart lands at $0 (e.g. fully covered by rewards / offers), skip the
      // native CashApp authorization round-trip entirely and commit the order directly.
      // The SDK would otherwise refuse to initialize with a non-positive total and block the
      // guest from checking out.
      const isFreeCashAppOrder = isPaymentType.cashApp && totals?.grandTotal === 0;
      const needsPaymentDetails =
        (isOneTimeCardPayment && !isPaymentProcessor.orbital) ||
        isPaymentProcessor.braintree ||
        (isPaymentType.cashApp && !isFreeCashAppOrder) ||
        isPaymentType.googlePay ||
        isPaymentType.applePay;

      let payload;
      if (isOrbitalOneTimeCardPayment) {
        payload = await buildCommitOrderPayloadWithoutAccount(undefined);
      } else if (needsPaymentDetails) {
        // request payment details in a generic manner from one time or 3rd party providers
        const details = await requestPaymentDetails();
        if (!details) {
          logger.info({
            isPaymentLog: true,
            message:
              '[useCheckoutSubmit][submitOrder]: User cancelled/dismissed the payment dialog',
            isPaymentType,
          });
          // User cancelled/dismissed the payment dialog
          return;
        }
        payload = await buildCommitOrderPayloadWithoutAccount(details);
      } else {
        payload = await buildCommitOrderPayloadWithAccount();
      }

      if (!payload) {
        logger.error({
          isPaymentLog: true,
          message: '[useCheckoutSubmit][submitOrder]: Empty order payload',
          submit_failure_payload_build: true,
          needsPaymentDetails,
          ...logContext,
        });
        openErrorDialog({ message: t('orderFailureMessage') });
        return;
      }

      setCheckoutState(prev => ({
        ...prev,
        isSubmittingOrder: true,
        wasCommitOrderCalled: true,
      }));
      commitOrder(payload);
    } catch (error) {
      logger.error({
        isPaymentLog: true,
        error,
        message: '[useCheckoutSubmit][submitOrder]: Error during order submission',
        ...logContext,
      });

      // Map PaymentRequestError to provider-specific error messages
      if (error instanceof PaymentRequestError) {
        let message = '';
        let showDialog = true;
        switch (error.errorType) {
          case paymentErrorType.applePayGeneric:
            message = t('orderFailureMessageForApplePay');
            break;
          case paymentErrorType.googlePayGeneric:
            message = t('orderFailureMessageForGooglePay');
            break;
          case paymentErrorType.cashAppGeneric:
            message = t('cashAppError');
            break;
          case paymentErrorType.cashAppDeclined:
            message = t('cashAppDecline');
            break;
          case paymentErrorType.applePayCancelled:
            showDialog = false;
            break;
          default:
            message = t('pspPaymentDeclinedTransactionForterTitle');
        }
        if (!showDialog) {
          return;
        }
        openErrorDialog({ message });
      } else {
        openErrorDialog({ message: t('orderFailureMessage') });
      }
    } finally {
      setCheckoutState(prev => ({
        ...prev,
        isBuildingCommitOrderPayload: false,
      }));
    }
  };

  return {
    navigateToOrderConfirmation,
    submitOrder,
    isCommitOrderLoading,
    ErrorDialog,
    wasCommitOrderCalled,
    commitOrderSuccess,
    commitOrderError,
  };
};
